// React and React Native Core imports
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

// 3rd party libraries
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useFormik, FormikProvider, useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

// Our Own imports
import Button from '../../atoms/Button';
import TextInputField from '../../atoms/TextInput/TextInputField';
import PhoneNumberInputField from '../../atoms/PhoneNumberInput/PhoneNumberInputField';
import { ComboboxField } from '../../atoms/Combobox';
import Combobox from '../../atoms/Combobox/Combobox';
import Avatar from '../../atoms/Avatar';
import TimezoneController from '../../pages/SettingsPage/GeneralSettings/TimezoneController';
import useUser from '../../utils/hooks/useUser/useUser';
import { NO_NOTIFICATIONS_TIER_NAME } from '../../utils/constants';

// Relative path imports
import './InviteModal.scss';

// Import queries
import {
  GET_ROLES_QUERY,
  GET_BARNS_QUERY,
  INVITE_USER_MUTATION,
  SEARCH_EXACT_EMAIL_GQL,
  GET_BARNS_BY_PK_QUERY,
  GET_ORG_PLAN_AND_TIERS_QUERY,
  UPSERT_USER_BARN_TIER_ASSIGNMENT_MUTATION,
  UPSERT_USER_ORG_TIER_ASSIGNMENT_MUTATION,
  UPSERT_USER_NOTIFICATION_TRANSPORT_SETTINGS_MUTATION,
} from './queries';
import NotificationTierSelector from '../NotificationTierSelector/NotificationTierSelector';
import ToggleSwitch from '../../atoms/ToggleSwitch/ToggleSwitch';

const getTierDefaultText = (planId, orgPlanAndTiersLoading) => {
  if (!planId) {
    return 'No notification plan configured';
  }

  if (orgPlanAndTiersLoading) {
    return 'Loading...';
  }

  return 'No Notifications';
};

const buildValidationSchema = ({ disableOrgInvites, disableBarnInvites, isFixedBarn }) =>
  Yup.object()
    .shape({
      name: Yup.string()
        .min(1, 'Name is required')
        .max(255, 'Name must not exceed 255 characters')
        .required('Name is required'),
      email: Yup.string()
        .email('Please enter a valid email address')
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address')
        .required('Email is required')
        .test(
          'not-self-invite',
          'You cannot invite yourself. Please enter a different email address.',
          function (value) {
            const { parent } = this;
            const loggedInUserEmail = parent._loggedInUserEmail;
            return !loggedInUserEmail || value !== loggedInUserEmail;
          },
        ),
      phoneNumber: Yup.string().test('valid-phone', 'Please enter a valid phone number', function (inputNumber) {
        if (!inputNumber) return true;
        const regex = /^(?:[0-9] ?){6,14}[0-9]$/;
        return regex.test(inputNumber);
      }),
      barnAccessList: Yup.array().of(
        Yup.object()
          .shape({
            barn_id: Yup.string().required(),
            role_id: Yup.string().nullable(),
            tier_id: Yup.string().nullable(),
          })
          .test('role-or-tier', 'Barn access requires a role or notification tier', (value) =>
            Boolean(value?.role_id || value?.tier_id),
          ),
      ),
    })
    .test('access-list', '', function (values) {
      const roleId = values?.role_id || '';
      const barnAccessList = values?.barnAccessList || [];
      const hasBarnAccess = isFixedBarn ? !!values?.barn_role_id : barnAccessList.length > 0;

      if (disableOrgInvites && disableBarnInvites) {
        return true;
      }

      if (disableOrgInvites && !hasBarnAccess) {
        return this.createError({
          path: 'accessList',
          message: 'Please add the user to at least one barn before inviting',
        });
      }

      if (disableBarnInvites && !roleId) {
        return this.createError({
          path: 'accessList',
          message: 'Please add the user to at least one organization before inviting',
        });
      }

      if (!disableOrgInvites && !disableBarnInvites && !roleId && !hasBarnAccess) {
        return this.createError({
          path: 'accessList',
          message: 'Please add the user to at least one organization or barn before inviting',
        });
      }

      return true;
    });

function RoleAndTierControls({
  roleFieldName,
  tierFieldName,
  roleItems,
  rolesLoading,
  roleDisabled = false,
  planId,
  tierItems,
  orgPlanAndTiersLoading,
  roleSelectClassName = 'InviteModal-orgRoleSelect',
  showClearButton = false,
  onClear,
  clearAriaLabel,
  isSubmitting,
  saving,
  additionalTierEnableRoleFields = [],
}) {
  const formik = useFormikContext();
  const selectedRoleId = formik.values[roleFieldName];
  const selectedTierId = formik.values[tierFieldName] || '';
  const canSelectTier = selectedRoleId || additionalTierEnableRoleFields.some((fieldName) => formik.values[fieldName]);

  return (
    <div className="InviteModal-roleControls">
      <ComboboxField
        disabled={isSubmitting || saving || rolesLoading || roleDisabled}
        name={roleFieldName}
        placeholder={rolesLoading ? 'Loading roles...' : 'Select Role'}
        label="Role"
        className={roleSelectClassName}
        items={roleItems}
      />
      <div className="InviteModal-tierSelect">
        <NotificationTierSelector
          planId={planId}
          tiers={tierItems}
          selectedTierId={selectedTierId}
          onTierChange={(tierId) => formik.setFieldValue(tierFieldName, tierId || null)}
          disabled={!canSelectTier || isSubmitting || saving || orgPlanAndTiersLoading || !planId}
          label="Notification Tier"
          defaultText={getTierDefaultText(planId, orgPlanAndTiersLoading)}
        />
      </div>
      {showClearButton && (
        <button
          type="button"
          className="InviteModal-clearAccessButton"
          onClick={onClear}
          disabled={isSubmitting || saving}
          aria-label={clearAriaLabel}
        >
          ×
        </button>
      )}
    </div>
  );
}

RoleAndTierControls.propTypes = {
  roleFieldName: PropTypes.string.isRequired,
  tierFieldName: PropTypes.string.isRequired,
  roleItems: PropTypes.array.isRequired,
  rolesLoading: PropTypes.bool,
  roleDisabled: PropTypes.bool,
  planId: PropTypes.string,
  tierItems: PropTypes.array,
  orgPlanAndTiersLoading: PropTypes.bool,
  roleSelectClassName: PropTypes.string,
  showClearButton: PropTypes.bool,
  onClear: PropTypes.func,
  clearAriaLabel: PropTypes.string,
  isSubmitting: PropTypes.bool,
  saving: PropTypes.bool,
  additionalTierEnableRoleFields: PropTypes.arrayOf(PropTypes.string),
};

// Toast handlers
const handleSuccess = (inviteeName) =>
  toast.success(`Successfully sent invite to ${inviteeName}`, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

const handleError = (error) => {
  const errorMessage =
    'An error has occurred while creating the invite. Please Contact Support@Feedflo.com if the issue persists..';

  console.error('Error creating invite:', error);
  Sentry?.captureException(error);

  toast.error(
    <div>
      <h4>Error</h4>
      <p>{errorMessage}</p>
    </div>,
    {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    },
  );
};

const asUserContext = {
  headers: {
    'x-hasura-role': 'user',
  },
};

function InviteModalView({
  onClose = () => {},
  onSuccess = () => {},
  selectedUser = null,
  prefilledUserData = null,
  organizationId = null,
  barnId = null,
  disableOrgInvites = false,
  disableBarnInvites = false,
}) {
  // --- State hooks ---
  const [suggestedUser, setSuggestedUser] = useState(null);
  const [existingUser, setExistingUser] = useState(selectedUser);

  // --- Atoms and other Hooks ---
  const { user: loggedInUser } = useUser();

  // --- Derived state ---
  const isNewUser = !prefilledUserData || prefilledUserData?.isNewUser || prefilledUserData?.isNewEmail;
  const defaultTimezone = loggedInUser?.timezone || dayjs.tz.guess();
  const isFixedBarn = !!barnId;

  const validationSchema = useMemo(
    () => buildValidationSchema({ disableOrgInvites, disableBarnInvites, isFixedBarn }),
    [disableOrgInvites, disableBarnInvites, isFixedBarn],
  );

  // --- Apollo/GraphQL hooks ---

  useEffect(() => {
    setExistingUser(selectedUser);
  }, [selectedUser]);

  const [inviteUser, { loading: saving }] = useMutation(INVITE_USER_MUTATION, {
    context: asUserContext,
  });

  const [upsertUserBarnTierAssignment] = useMutation(UPSERT_USER_BARN_TIER_ASSIGNMENT_MUTATION, {
    context: asUserContext,
  });

  const [upsertUserOrgTierAssignment] = useMutation(UPSERT_USER_ORG_TIER_ASSIGNMENT_MUTATION, {
    context: asUserContext,
  });

  const [upsertUserNotificationTransportSettings] = useMutation(UPSERT_USER_NOTIFICATION_TRANSPORT_SETTINGS_MUTATION, {
    context: asUserContext,
  });

  const { data: rolesData, loading: rolesLoading } = useQuery(GET_ROLES_QUERY, {
    context: asUserContext,
    variables: {
      orgId: organizationId,
    },
    skip: !organizationId,
  });

  const { data: barnsByIdData, loading: barnsByIdLoading } = useQuery(GET_BARNS_BY_PK_QUERY, {
    context: asUserContext,
    variables: {
      barnId: barnId,
    },
    skip: !barnId,
  });

  const { data: barnsByOrgData, loading: barnsByOrgLoading } = useQuery(GET_BARNS_QUERY, {
    context: asUserContext,
    variables: {
      orgId: organizationId,
    },
    skip: !organizationId,
  });

  const { data: orgPlanAndTiersData, loading: orgPlanAndTiersLoading } = useQuery(GET_ORG_PLAN_AND_TIERS_QUERY, {
    context: asUserContext,
    variables: {
      orgId: organizationId,
    },
    skip: !organizationId,
    fetchPolicy: 'network-only',
  });

  const planId = orgPlanAndTiersData?.organization_notification_plan?.[0]?.plan_id;
  const planTiers = orgPlanAndTiersData?.organization_notification_plan?.[0]?.plan?.tiers || [];

  const tierItems = useMemo(
    () =>
      planTiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        sort_order: tier.sort_order,
        description: tier.public_description,
        public_description: tier.public_description,
      })),
    [planTiers],
  );

  const defaultTierId = useMemo(() => {
    if (!planTiers.length) return null;
    const sorted = [...planTiers].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const noNotificationsTier = sorted.find((tier) => tier.name === NO_NOTIFICATIONS_TIER_NAME);
    return noNotificationsTier?.id || sorted[0]?.id || null;
  }, [planTiers]);

  const barnsLoading = barnsByIdLoading || barnsByOrgLoading;

  const formik = useFormik({
    initialValues: {
      name: selectedUser?.name || prefilledUserData?.name || '',
      email: selectedUser?.email || prefilledUserData?.email || '',
      phoneNumber: selectedUser?.phone_number || prefilledUserData?.phone_number || '',
      timezone: selectedUser?.timezone || prefilledUserData?.timezone || defaultTimezone,
      barnAccessList: [],
      role_id: '',
      org_tier_id: null,
      barn_id: barnId ? barnId : '',
      barn_role_id: '',
      barn_tier_id: null,
      transport_sms: false,
      transport_email: false,
      transport_push: false,
      _loggedInUserEmail: loggedInUser?.email,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, formikBag) => {
      let effectiveBarnAccessList;
      if (isFixedBarn) {
        if (values.barn_role_id) {
          effectiveBarnAccessList = [{ barn_id: barnId, role_id: values.barn_role_id, tier_id: values.barn_tier_id }];
        } else if (values.barn_tier_id && barnId && values.role_id) {
          effectiveBarnAccessList = [{ barn_id: barnId, role_id: null, tier_id: values.barn_tier_id }];
        } else {
          effectiveBarnAccessList = [];
        }
      } else {
        effectiveBarnAccessList = values.barnAccessList;
      }

      try {
        const orgs = values.role_id && organizationId ? [{ orgId: organizationId, roleId: values.role_id }] : [];

        const barns = effectiveBarnAccessList
          .filter((access) => access.role_id)
          .map((access) => ({
            barnId: access.barn_id,
            roleId: access.role_id,
          }));

        const { data, errors } = await inviteUser({
          variables: {
            userName: values.name,
            userEmail: values.email,
            userPhoneNumber: values.phoneNumber,
            userTimezone: values.timezone,
            orgs,
            barns,
          },
        });

        if (errors?.[0]?.extensions?.code === 409) {
          toast.success(`Added ${values.name}!`, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }

        const userId = data?.inviteUser?.userId;
        let settingsSaveFailed = false;

        if (userId) {
          if (values.role_id && organizationId && values.org_tier_id) {
            try {
              await upsertUserOrgTierAssignment({
                variables: {
                  objects: [
                    {
                      user_id: userId,
                      organization_id: organizationId,
                      tier_id: values.org_tier_id,
                    },
                  ],
                },
              });
            } catch (error) {
              console.error('Error creating org tier assignments:', error);
              Sentry?.captureException(error);
              settingsSaveFailed = true;
            }
          }

          const barnTierAssignments = effectiveBarnAccessList
            .filter((access) => access.tier_id)
            .map((access) => ({
              user_id: userId,
              barn_id: access.barn_id,
              tier_id: access.tier_id,
            }));

          if (barnTierAssignments.length > 0) {
            try {
              await upsertUserBarnTierAssignment({
                variables: {
                  objects: barnTierAssignments,
                },
              });
            } catch (error) {
              console.error('Error creating barn tier assignments:', error);
              Sentry?.captureException(error);
              settingsSaveFailed = true;
            }
          }

          if (values.transport_sms || values.transport_email || values.transport_push) {
            try {
              await upsertUserNotificationTransportSettings({
                variables: {
                  user_id: userId,
                  sms: values.transport_sms,
                  email: values.transport_email,
                  push: values.transport_push,
                },
              });
            } catch (error) {
              console.error('Error creating transport settings:', error);
              Sentry?.captureException(error);
              settingsSaveFailed = true;
            }
          }
        }

        handleSuccess(values.name);

        if (settingsSaveFailed) {
          toast.error('Invite sent, but some settings could not be saved', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }

        onSuccess({
          inviteId: data?.inviteUser?.id,
          barns,
          orgs,
          user: {
            name: values.name,
            email: values.email,
            id: data?.inviteUser?.userId,
          },
        });
        formikBag.resetForm();
        onCloseDialog({ success: true, cancel: false, error: false });
      } catch (error) {
        onCloseDialog({ success: false, cancel: false, error: true });
        if (error?.message?.includes('Cannot invite user if they already have access')) {
          toast.success(`Added ${values?.name}!`, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        } else {
          console.error('Error creating invite:', error);
          handleError(error);
        }
      }
    },
  });

  const { data: exactEmailData, loading: exactEmailLoading } = useQuery(SEARCH_EXACT_EMAIL_GQL, {
    context: asUserContext,
    variables: {
      email: formik.values.email,
    },
    skip:
      !formik.values.email ||
      !formik.values.email.includes('.') ||
      !formik.values.email.includes('@') ||
      formik.values.email?.trim().length < 6 ||
      existingUser,
  });

  // --- Derived state ---
  const availableRoles = rolesData?.roles || [];
  const roleItems = useMemo(
    () => availableRoles.map((role) => ({ id: role.id, name: role.name, value: role.id })),
    [availableRoles],
  );
  const availableBarns = barnsByOrgData?.farm || barnsByIdData?.farm || [];
  const isSelfInvite = formik.values.email === loggedInUser?.email;
  const isPhoneNumberValid = formik.values.phoneNumber?.trim()?.length > 1 && !formik.errors.phoneNumber;

  const { barnAccessList } = formik.values;

  const canCreateOrgInvite = !disableOrgInvites && !!formik.values.role_id;
  const canCreateBarnInvite =
    !disableBarnInvites && (isFixedBarn ? !!formik.values.barn_role_id : barnAccessList.length > 0);
  const hasValidInvite = canCreateOrgInvite || canCreateBarnInvite;
  const hasOrgAccessSelection = !!formik.values.role_id;
  const hasBarnAccessSelection = !!formik.values.barn_role_id;

  const mapRolesWithDisabled = (roles, disabledRoleIds) =>
    roles.map((role) => {
      const alreadyHasRole = disabledRoleIds.includes(role.id);
      return {
        id: role.id,
        name: alreadyHasRole ? `${role.name} (Already Added)` : role.name,
        value: role.id,
        disabled: alreadyHasRole,
      };
    });

  const getBarnRolesForRow = (rowIndex, rowBarnId) => {
    const disabledRoleIds = barnAccessList
      .filter((access, index) => index !== rowIndex && access.barn_id === rowBarnId)
      .map((access) => access.role_id);

    return mapRolesWithDisabled(availableRoles, disabledRoleIds);
  };

  const stagingBarnRoles = availableRoles.map((role) => ({
    id: role.id,
    name: role.name,
    value: role.id,
  }));

  // --- Navigation/side effects ---
  useEffect(() => {
    if (!selectedUser) {
      setExistingUser(null);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (!exactEmailLoading) {
      if (exactEmailData?.user?.[0] && !existingUser) {
        setSuggestedUser(exactEmailData.user[0]);
      } else {
        setSuggestedUser(null);
      }
    }
  }, [exactEmailLoading, exactEmailData, existingUser]);

  useEffect(() => {
    if (!defaultTierId) {
      return;
    }

    if (!formik.values.org_tier_id) {
      formik.setFieldValue('org_tier_id', defaultTierId);
    }

    if (!formik.values.barn_tier_id) {
      formik.setFieldValue('barn_tier_id', defaultTierId);
    }
  }, [defaultTierId]);

  // Re-run root-level access-list validation when org/barn access changes after a failed submit.
  useEffect(() => {
    if (formik.errors.accessList || formik.submitCount > 0) {
      void formik.validateForm();
    }
  }, [formik.values.barnAccessList, formik.values.role_id, formik.values.barn_role_id]);

  // --- Function definitions ---

  const onCloseDialog = ({ cancel, success, error } = { cancel: null, success: null, error: null }) => {
    formik.resetForm();
    setSuggestedUser(null);
    setExistingUser(null);
    onClose({ cancel, success, error });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseDialog({ cancel: true });
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addBarnAccess = () => {
    const hasBarnRole = !!formik.values.barn_role_id;
    const canAddTierOnly = !hasBarnRole && !!formik.values.role_id;

    if (!formik.values.barn_id || disableBarnInvites || (!hasBarnRole && !canAddTierOnly)) {
      return;
    }

    const isDuplicate = barnAccessList.some(
      (access) =>
        access.barn_id === formik.values.barn_id &&
        (access.role_id || null) === (formik.values.barn_role_id || null),
    );
    if (isDuplicate) {
      toast.error(
        hasBarnRole
          ? 'This barn and role combination is already added'
          : 'This barn already has a notification tier added',
      );
      return;
    }

    const newAccess = {
      barn_id: formik.values.barn_id,
      role_id: formik.values.barn_role_id || null,
      tier_id: formik.values.barn_tier_id || defaultTierId,
    };

    formik.setFieldValue('barnAccessList', [...barnAccessList, newAccess], false);

    if (!barnId) {
      formik.setFieldValue('barn_id', '', false);
    }
    formik.setFieldValue('barn_role_id', '', false);
    formik.setFieldValue('barn_tier_id', defaultTierId, false);
    void formik.validateForm();
  };

  const removeBarnAccess = (index) => {
    formik.setFieldValue(
      'barnAccessList',
      barnAccessList.filter((_, i) => i !== index),
      false,
    );
    void formik.validateForm();
  };

  const clearOrgAccess = () => {
    formik.setFieldValue('role_id', '', false);
    formik.setFieldValue('org_tier_id', defaultTierId || null, false);
    void formik.validateForm();
  };

  const clearBarnAccess = () => {
    formik.setFieldValue('barn_role_id', '', false);
    formik.setFieldValue('barn_tier_id', defaultTierId || null, false);
    void formik.validateForm();
  };

  const handleSuggestionClick = () => {
    if (suggestedUser) {
      formik.setValues({
        ...formik.values,
        name: suggestedUser.name,
        email: suggestedUser.email,
        phoneNumber: suggestedUser.phone_number.replace(/[+()\s-]/g, '') || '',
        timezone: suggestedUser.timezone || defaultTimezone,
      });

      setExistingUser({
        ...suggestedUser,
      });

      setSuggestedUser(null);
    }
  };

  // --- Render ---
  return (
    <div
      className="InviteModal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCloseDialog({ cancel: true });
        }
      }}
    >
      <div className="InviteModal" role="dialog" aria-modal="true">
        {barnsLoading ? (
          <span>loading...</span>
        ) : (
          <div className="InviteModal-content">
            <div className="InviteModal-header">
              <h2>{isNewUser ? 'Invite New User' : 'Invite Existing User'}</h2>
              <button className="InviteModal-closeButton" onClick={() => onCloseDialog({ cancel: true })} type="button">
                X
              </button>
            </div>

            <FormikProvider value={formik}>
              <form className="InviteModal-form" onSubmit={formik.handleSubmit}>
                <div className="InviteModal-body">
                  <div className={`InviteModal-section ${disableOrgInvites && 'InviteModal-noBarnHeader'}`}>
                    {existingUser ? (
                      <>
                        <div className="InviteModal-existingUser">
                          <div className="InviteModal-userInfo">
                            <Avatar src={existingUser.avatar} alt={existingUser.name} size="medium" />
                            <div className="InviteModal-userDetails">
                              <h4>{existingUser.name}</h4>
                              <p className="InviteModal-userEmail">{existingUser.email}</p>
                              {existingUser.phone_number && (
                                <p className="InviteModal-userPhone">{existingUser.phone_number}</p>
                              )}
                            </div>
                          </div>
                          <p className="InviteModal-existingUserNote">
                            This user already exists in the system. You can invite them to this organization by
                            selecting a role.
                          </p>
                        </div>
                        <Button
                          type="button"
                          disabled={formik.isSubmitting || saving}
                          onClick={() => {
                            setExistingUser(null);
                            formik.setFieldValue('name', '');
                            formik.setFieldValue('email', '');
                            formik.setFieldValue('phoneNumber', '');
                            formik.setFieldValue('timezone', '');
                          }}
                          variant="text"
                          color="secondary"
                          content="Mean someone else? Click to unset."
                          className="InviteModal-addButton"
                        />
                      </>
                    ) : null}

                    {!existingUser && (
                      <div className="InviteModal-newUserSection">
                        <div className="InviteModal-emailWrapper">
                          <TextInputField
                            name="email"
                            label="Email *"
                            type="email"
                            placeholder="Enter email address"
                            disabled={formik.isSubmitting || saving}
                          />

                          {suggestedUser && !isSelfInvite && (
                            <div className="InviteModal-suggestion">
                              <button
                                type="button"
                                onClick={handleSuggestionClick}
                                className="InviteModal-suggestionButton"
                                disabled={formik.isSubmitting || saving}
                              >
                                Did you mean <strong>{suggestedUser.name}</strong>?
                              </button>
                            </div>
                          )}
                        </div>

                        <TextInputField
                          name="name"
                          label="Name *"
                          placeholder="Enter full name"
                          disabled={formik.isSubmitting || saving}
                        />

                        <PhoneNumberInputField
                          name="phoneNumber"
                          label="Phone Number"
                          disabled={formik.isSubmitting || saving}
                          description="Required for SMS Notifications"
                        />

                        <TimezoneController
                          timezone={formik.values.timezone}
                          disabled={formik.isSubmitting || saving}
                          onChange={(tz) => {
                            formik.setFieldValue('timezone', tz.name);
                          }}
                        />

                        <div className="InviteModal-transport">
                          <label className="InviteModal-transportLabel">
                            Notification Methods{' '}
                            <span className="InviteModal-transportNote">
                              (Note: Notifications will start immediately)
                            </span>
                          </label>

                          <div className="InviteModal-transportToggles">
                            <div className="InviteModal-smsWrapper">
                              <ToggleSwitch
                                isActive={formik.values.transport_sms}
                                onToggle={(checked) => formik.setFieldValue('transport_sms', checked)}
                                disabled={formik.isSubmitting || saving || !isPhoneNumberValid}
                                direction="vertical"
                                textLeft={!isPhoneNumberValid ? 'SMS 🚫' : 'SMS'}
                              />
                            </div>
                            <ToggleSwitch
                              isActive={formik.values.transport_email}
                              onToggle={(checked) => formik.setFieldValue('transport_email', checked)}
                              disabled={formik.isSubmitting || saving}
                              direction="vertical"
                              textLeft="Email"
                            />
                            <ToggleSwitch
                              isActive={formik.values.transport_push}
                              onToggle={(checked) => formik.setFieldValue('transport_push', checked)}
                              disabled={formik.isSubmitting || saving}
                              direction="vertical"
                              textLeft="App/Push"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="InviteModal-accessWrapper">
                    {!disableOrgInvites && (
                      <div className="InviteModal-section InviteModal-accessSection">
                        <h3 className="InviteModal-sectionHeader">Organization Access</h3>
                        <p className="InviteModal-sectionDescription">
                          Select a role and notification tier for the invitee.
                        </p>

                        <RoleAndTierControls
                          roleFieldName="role_id"
                          tierFieldName="org_tier_id"
                          roleItems={roleItems}
                          rolesLoading={rolesLoading}
                          planId={planId}
                          tierItems={tierItems}
                          orgPlanAndTiersLoading={orgPlanAndTiersLoading}
                          showClearButton={hasOrgAccessSelection}
                          onClear={clearOrgAccess}
                          clearAriaLabel="Clear organization access"
                          isSubmitting={formik.isSubmitting}
                          saving={saving}
                        />
                      </div>
                    )}

                    {!disableBarnInvites && (
                      <div className="InviteModal-section InviteModal-accessSection">
                        {!disableOrgInvites && (
                          <>
                            <h3 className="InviteModal-sectionHeader">Barn Access</h3>
                            <p className="InviteModal-sectionDescription">Add barn access for the invitee.</p>
                          </>
                        )}

                        {isFixedBarn ? (
                          <RoleAndTierControls
                            roleFieldName="barn_role_id"
                            tierFieldName="barn_tier_id"
                            roleItems={stagingBarnRoles}
                            rolesLoading={rolesLoading}
                            planId={planId}
                            tierItems={tierItems}
                            orgPlanAndTiersLoading={orgPlanAndTiersLoading}
                            roleSelectClassName="InviteModal-roleSelect"
                            showClearButton={hasBarnAccessSelection}
                            onClear={clearBarnAccess}
                            clearAriaLabel="Clear barn access"
                            isSubmitting={formik.isSubmitting}
                            saving={saving}
                            additionalTierEnableRoleFields={['role_id']}
                          />
                        ) : (
                          <>
                            <div className="InviteModal-barnControls">
                              <Combobox
                                disabled={formik.isSubmitting || saving || barnsLoading}
                                placeholder={barnsLoading ? 'Loading barns...' : 'Select Barn'}
                                label="Barn"
                                className="InviteModal-barnSelect"
                                loading={barnsLoading}
                                items={availableBarns}
                                value={formik.values.barn_id}
                                onChange={(selectedBarn) => {
                                  formik.setFieldValue('barn_id', selectedBarn?.id || '');
                                  formik.setFieldValue('barn_role_id', '');
                                  formik.setFieldValue('barn_tier_id', defaultTierId);
                                }}
                              />
                              <ComboboxField
                                disabled={formik.isSubmitting || saving || rolesLoading || !formik.values?.barn_id}
                                name="barn_role_id"
                                placeholder={rolesLoading ? 'Loading roles...' : 'Select Role'}
                                label="Role"
                                className="InviteModal-roleSelect"
                                items={stagingBarnRoles}
                              />
                              <div className="InviteModal-tierSelect">
                                <NotificationTierSelector
                                  planId={planId}
                                  tiers={tierItems}
                                  selectedTierId={formik.values.barn_tier_id || ''}
                                  onTierChange={(tierId) => formik.setFieldValue('barn_tier_id', tierId || null)}
                                  disabled={
                                    formik.isSubmitting ||
                                    !formik.values?.barn_id ||
                                    (!formik.values?.barn_role_id && !formik.values?.role_id) ||
                                    saving ||
                                    orgPlanAndTiersLoading ||
                                    !planId
                                  }
                                  label="Notification Tier"
                                  defaultText={getTierDefaultText(planId, orgPlanAndTiersLoading)}
                                />
                              </div>
                              <Button
                                type="button"
                                disabled={
                                  formik.isSubmitting ||
                                  saving ||
                                  !formik.values.barn_id ||
                                  (!formik.values.barn_role_id && !formik.values.role_id)
                                }
                                onClick={addBarnAccess}
                                variant="vivid"
                                color="success"
                                content="Add"
                                className="InviteModal-addAccessButton"
                              />
                            </div>

                            {barnAccessList.length > 0 && (
                              <div className="InviteModal-accessList">
                                {barnAccessList.map((access, index) => (
                                  <div key={index} className="InviteModal-accessItem">
                                    <div className="InviteModal-accessItem-controls">
                                      <Combobox
                                        disabled={formik.isSubmitting || saving || barnsLoading}
                                        placeholder={barnsLoading ? 'Loading barns...' : 'Select Barn'}
                                        label="Barn"
                                        className="InviteModal-barnSelect"
                                        loading={barnsLoading}
                                        items={availableBarns}
                                        value={access.barn_id}
                                        onChange={(selectedBarn) => {
                                          formik.setFieldValue(
                                            `barnAccessList[${index}].barn_id`,
                                            selectedBarn?.id || '',
                                          );
                                          formik.setFieldValue(`barnAccessList[${index}].role_id`, '');
                                          formik.setFieldValue(`barnAccessList[${index}].tier_id`, defaultTierId);
                                        }}
                                      />
                                      <ComboboxField
                                        disabled={formik.isSubmitting || saving || rolesLoading || !access.barn_id}
                                        name={`barnAccessList[${index}].role_id`}
                                        placeholder={rolesLoading ? 'Loading roles...' : 'Select Role'}
                                        label="Role"
                                        className="InviteModal-roleSelect"
                                        items={getBarnRolesForRow(index, access.barn_id)}
                                      />
                                      <div className="InviteModal-tierSelect">
                                        <NotificationTierSelector
                                          planId={planId}
                                          tiers={tierItems}
                                          selectedTierId={access.tier_id || ''}
                                          onTierChange={(tierId) =>
                                            formik.setFieldValue(`barnAccessList[${index}].tier_id`, tierId || null)
                                          }
                                          disabled={
                                            formik.isSubmitting ||
                                            !access.barn_id ||
                                            (!access.role_id && !formik.values.role_id) ||
                                            saving ||
                                            orgPlanAndTiersLoading ||
                                            !planId
                                          }
                                          label="Notification Tier"
                                          defaultText={getTierDefaultText(planId, orgPlanAndTiersLoading)}
                                        />
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeBarnAccess(index)}
                                      className="InviteModal-removeButton"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {formik.errors.accessList && !hasValidInvite && (
                  <div className="InviteModal-errors">
                    <p>{formik.errors.accessList}</p>
                  </div>
                )}

                <div className="InviteModal-footer">
                  <Button
                    type="button"
                    onClick={() => onCloseDialog({ cancel: true })}
                    disabled={formik.isSubmitting || saving}
                    content="Cancel"
                    className="InviteModal-cancelButton"
                  />

                  <Button
                    type="submit"
                    disabled={formik.isSubmitting || saving || !formik.isValid || !hasValidInvite}
                    variant="vivid"
                    color="success"
                    content={saving ? 'Sending Invite...' : 'Invite'}
                    className="InviteModal-submitButton"
                  />
                </div>
              </form>
            </FormikProvider>
          </div>
        )}
      </div>
    </div>
  );
}

function InviteModal({
  open = false,
  onClose = () => {},
  onSuccess = () => {},
  selectedUser = null,
  prefilledUserData = null,
  organizationId = null,
  barnId = null,
  disableOrgInvites = false,
  disableBarnInvites = false,
}) {
  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <InviteModalView
      onClose={onClose}
      onSuccess={onSuccess}
      selectedUser={selectedUser}
      prefilledUserData={prefilledUserData}
      organizationId={organizationId}
      barnId={barnId}
      disableBarnInvites={disableBarnInvites}
      disableOrgInvites={disableOrgInvites}
    />,
    document.getElementById('portal-root'),
  );
}

InviteModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  selectedUser: PropTypes.object,
  prefilledUserData: PropTypes.object,
  organizationId: PropTypes.string,
  barnId: PropTypes.string,
  disableBarnInvites: PropTypes.bool,
  disableOrgInvites: PropTypes.bool,
};

InviteModalView.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  selectedUser: PropTypes.object,
  prefilledUserData: PropTypes.object,
  organizationId: PropTypes.string,
  barnId: PropTypes.string,
  disableBarnInvites: PropTypes.bool,
  disableOrgInvites: PropTypes.bool,
};

export default InviteModal;
